import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { API } from '../config/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Shield, 
  Play, 
  Download, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Loader2,
  Globe,
  Server,
  FileText,
  Zap,
  Eye,
  Clock,
  Bug,
  Lightbulb,
  Calendar,
  Bell,
  Mail,
  Settings,
  Save,
  Plus,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';

// Routes to audit by category
const PUBLIC_ROUTES = [
  { path: '/', name: 'Home (Landing)' },
  { path: '/home', name: 'Home' },
  { path: '/spotlight', name: 'Spotlight Feed' },
  { path: '/charts', name: 'Charts' },
  { path: '/discover', name: 'Discover' },
  { path: '/clips', name: 'Soundstage Clips' },
  { path: '/playlists', name: 'Playlists' },
  { path: '/leaderboard', name: 'Leaderboard' },
  { path: '/creatives', name: 'Creatives' },
  { path: '/interviews', name: 'Interviews' },
  { path: '/events', name: 'Events' },
  { path: '/perks', name: 'Perks' },
  { path: '/terms', name: 'Terms of Service' },
  { path: '/privacy', name: 'Privacy Policy' },
  { path: '/community-guidelines', name: 'Community Guidelines' },
  { path: '/support', name: 'Support' },
];

const AUTH_ROUTES = [
  { path: '/auth', name: 'Auth/Login' },
  { path: '/signup', name: 'Signup' },
  { path: '/forgot-password', name: 'Forgot Password' },
];

const DASHBOARD_ROUTES = [
  { path: '/artist/dashboard', name: 'Artist Dashboard' },
  { path: '/settings', name: 'Settings' },
  { path: '/messages', name: 'Messages' },
  { path: '/notifications', name: 'Notifications' },
  { path: '/dashboard/requests', name: 'Requests Inbox' },
];

const ADMIN_ROUTES = [
  { path: '/admin', name: 'Admin Control Center' },
  { path: '/admin/health-audit', name: 'Health Audit' },
];

// API endpoints to test
const API_ENDPOINTS = [
  { method: 'GET', path: '/health', name: 'Health Check', auth: false },
  { method: 'GET', path: '/feed', name: 'Feed', auth: true },
  { method: 'GET', path: '/tracks', name: 'Tracks List', auth: false },
  { method: 'GET', path: '/artists', name: 'Artists List', auth: false },
  { method: 'GET', path: '/videos', name: 'Videos List', auth: false },
  { method: 'GET', path: '/charts/top-20', name: 'Top 20 Charts', auth: false },
  { method: 'GET', path: '/me/notifications', name: 'Notifications', auth: true },
  { method: 'GET', path: '/messages/conversations', name: 'Conversations', auth: true },
  { method: 'GET', path: '/requests/inbox', name: 'Requests Inbox', auth: true },
  { method: 'GET', path: '/auth/me', name: 'Current User', auth: true },
];

export default function HealthAuditPage() {
  const { user, token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  // API Audit State
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTest, setCurrentTest] = useState('');
  const [results, setResults] = useState(null);
  const [logs, setLogs] = useState([]);
  const logsEndRef = useRef(null);
  
  // E2E Audit State
  const [isRunningE2E, setIsRunningE2E] = useState(false);
  const [e2eResults, setE2EResults] = useState(null);
  const [e2eLogs, setE2ELogs] = useState([]);
  
  // Schedule State
  const [schedule, setSchedule] = useState({
    enabled: false,
    frequency: 'daily',
    hour: 3,
    day_of_week: 0,
    notify_on_critical: true,
    notify_on_any_failure: false,
    notification_emails: [],
    last_run: null,
    next_run: null
  });
  const [newEmail, setNewEmail] = useState('');
  const [savingSchedule, setSavingSchedule] = useState(false);

  // Check admin access
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    // Only platform owner can access
    if (user?.role !== 'owner' && user?.email !== 'itzchopbusiness1@gmail.com') {
      toast.error('Access denied. Admin only.');
      navigate('/');
    }
    
    // Load latest E2E report and schedule
    loadLatestE2EReport();
    loadSchedule();
  }, [isAuthenticated, user, navigate]);

  // Auto-scroll logs
  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs, e2eLogs]);

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { timestamp, message, type }]);
  };
  
  const addE2ELog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setE2ELogs(prev => [...prev, { timestamp, message, type }]);
  };

  const loadLatestE2EReport = async () => {
    try {
      const response = await fetch(`${API}/admin/health-audit/latest`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.report) {
          setE2EResults(data.report);
        }
      }
    } catch (err) {
      console.error('Failed to load latest E2E report:', err);
    }
  };

  const loadSchedule = async () => {
    try {
      const response = await fetch(`${API}/admin/health-audit/schedule`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.schedule) {
          setSchedule(data.schedule);
        }
      }
    } catch (err) {
      console.error('Failed to load schedule:', err);
    }
  };

  const saveSchedule = async () => {
    setSavingSchedule(true);
    try {
      const response = await fetch(`${API}/admin/health-audit/schedule`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          enabled: schedule.enabled,
          frequency: schedule.frequency,
          hour: schedule.hour,
          day_of_week: schedule.day_of_week,
          notify_on_critical: schedule.notify_on_critical,
          notify_on_any_failure: schedule.notify_on_any_failure,
          notification_emails: schedule.notification_emails
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSchedule(data.schedule);
        toast.success('Schedule saved successfully!');
      } else {
        toast.error(data.detail || 'Failed to save schedule');
      }
    } catch (err) {
      console.error('Failed to save schedule:', err);
      toast.error('Failed to save schedule');
    } finally {
      setSavingSchedule(false);
    }
  };

  const addNotificationEmail = () => {
    if (!newEmail) return;
    
    // Validate email
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailPattern.test(newEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    if (schedule.notification_emails.includes(newEmail)) {
      toast.error('Email already added');
      return;
    }
    
    setSchedule(prev => ({
      ...prev,
      notification_emails: [...prev.notification_emails, newEmail]
    }));
    setNewEmail('');
  };

  const removeNotificationEmail = (email) => {
    setSchedule(prev => ({
      ...prev,
      notification_emails: prev.notification_emails.filter(e => e !== email)
    }));
  };

  const testRoute = async (route, requiresAuth = false) => {
    try {
      const url = `${window.location.origin}${route.path}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: requiresAuth && token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });
      
      return {
        path: route.path,
        name: route.name,
        status: response.status,
        ok: response.ok || response.status === 200 || response.status === 302,
        error: null
      };
    } catch (err) {
      return {
        path: route.path,
        name: route.name,
        status: 0,
        ok: false,
        error: err.message
      };
    }
  };

  const testApiEndpoint = async (endpoint) => {
    try {
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (endpoint.auth && token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API}${endpoint.path}`, {
        method: endpoint.method,
        headers
      });
      
      let errorDetail = null;
      
      try {
        const data = await response.json();
        if (!response.ok) {
          errorDetail = data.detail || data.message || JSON.stringify(data);
        }
      } catch {
        // Response wasn't JSON
      }
      
      return {
        path: endpoint.path,
        name: endpoint.name,
        method: endpoint.method,
        status: response.status,
        ok: response.ok,
        error: errorDetail,
        requiresAuth: endpoint.auth
      };
    } catch (err) {
      return {
        path: endpoint.path,
        name: endpoint.name,
        method: endpoint.method,
        status: 0,
        ok: false,
        error: err.message,
        requiresAuth: endpoint.auth
      };
    }
  };

  // Run API-level quick audit
  const runQuickAudit = async () => {
    setIsRunning(true);
    setProgress(0);
    setLogs([]);
    setResults(null);
    
    const auditResults = {
      timestamp: new Date().toISOString(),
      publicRoutes: [],
      authRoutes: [],
      dashboardRoutes: [],
      adminRoutes: [],
      apiEndpoints: [],
      summary: {
        totalTests: 0,
        passed: 0,
        failed: 0,
        warnings: 0
      }
    };
    
    const allTests = [
      ...PUBLIC_ROUTES.map(r => ({ ...r, category: 'public' })),
      ...AUTH_ROUTES.map(r => ({ ...r, category: 'auth' })),
      ...DASHBOARD_ROUTES.map(r => ({ ...r, category: 'dashboard' })),
      ...ADMIN_ROUTES.map(r => ({ ...r, category: 'admin' })),
      ...API_ENDPOINTS.map(e => ({ ...e, category: 'api' }))
    ];
    
    const totalTests = allTests.length;
    
    addLog('Starting quick API audit...', 'info');
    addLog(`Total tests to run: ${totalTests}`, 'info');
    
    // Test public routes
    addLog('--- Testing Public Routes ---', 'info');
    for (const route of PUBLIC_ROUTES) {
      setCurrentTest(`Testing ${route.name}...`);
      const result = await testRoute(route, false);
      auditResults.publicRoutes.push(result);
      
      if (result.ok) {
        addLog(`✓ ${route.name} (${route.path}) - ${result.status}`, 'success');
        auditResults.summary.passed++;
      } else {
        addLog(`✗ ${route.name} (${route.path}) - ${result.status} ${result.error || ''}`, 'error');
        auditResults.summary.failed++;
      }
      
      auditResults.summary.totalTests++;
      setProgress((auditResults.summary.totalTests / totalTests) * 100);
      await new Promise(r => setTimeout(r, 50));
    }
    
    // Test auth routes
    addLog('--- Testing Auth Routes ---', 'info');
    for (const route of AUTH_ROUTES) {
      setCurrentTest(`Testing ${route.name}...`);
      const result = await testRoute(route, false);
      auditResults.authRoutes.push(result);
      
      if (result.ok) {
        addLog(`✓ ${route.name} (${route.path}) - ${result.status}`, 'success');
        auditResults.summary.passed++;
      } else {
        addLog(`✗ ${route.name} (${route.path}) - ${result.status} ${result.error || ''}`, 'error');
        auditResults.summary.failed++;
      }
      
      auditResults.summary.totalTests++;
      setProgress((auditResults.summary.totalTests / totalTests) * 100);
      await new Promise(r => setTimeout(r, 50));
    }
    
    // Test dashboard routes (requires auth)
    addLog('--- Testing Dashboard Routes (Authenticated) ---', 'info');
    for (const route of DASHBOARD_ROUTES) {
      setCurrentTest(`Testing ${route.name}...`);
      const result = await testRoute(route, true);
      auditResults.dashboardRoutes.push(result);
      
      if (result.ok) {
        addLog(`✓ ${route.name} (${route.path}) - ${result.status}`, 'success');
        auditResults.summary.passed++;
      } else {
        addLog(`✗ ${route.name} (${route.path}) - ${result.status} ${result.error || ''}`, 'error');
        auditResults.summary.failed++;
      }
      
      auditResults.summary.totalTests++;
      setProgress((auditResults.summary.totalTests / totalTests) * 100);
      await new Promise(r => setTimeout(r, 50));
    }
    
    // Test admin routes
    addLog('--- Testing Admin Routes ---', 'info');
    for (const route of ADMIN_ROUTES) {
      setCurrentTest(`Testing ${route.name}...`);
      const result = await testRoute(route, true);
      auditResults.adminRoutes.push(result);
      
      if (result.ok) {
        addLog(`✓ ${route.name} (${route.path}) - ${result.status}`, 'success');
        auditResults.summary.passed++;
      } else {
        addLog(`✗ ${route.name} (${route.path}) - ${result.status} ${result.error || ''}`, 'error');
        auditResults.summary.failed++;
      }
      
      auditResults.summary.totalTests++;
      setProgress((auditResults.summary.totalTests / totalTests) * 100);
      await new Promise(r => setTimeout(r, 50));
    }
    
    // Test API endpoints
    addLog('--- Testing API Endpoints ---', 'info');
    for (const endpoint of API_ENDPOINTS) {
      setCurrentTest(`Testing API: ${endpoint.name}...`);
      const result = await testApiEndpoint(endpoint);
      auditResults.apiEndpoints.push(result);
      
      if (result.ok) {
        addLog(`✓ ${endpoint.method} ${endpoint.path} - ${result.status}`, 'success');
        auditResults.summary.passed++;
      } else {
        addLog(`✗ ${endpoint.method} ${endpoint.path} - ${result.status} ${result.error || ''}`, 'error');
        auditResults.summary.failed++;
      }
      
      auditResults.summary.totalTests++;
      setProgress((auditResults.summary.totalTests / totalTests) * 100);
      await new Promise(r => setTimeout(r, 50));
    }
    
    // Finalize
    addLog('--- Quick Audit Complete ---', 'info');
    addLog(`Total: ${auditResults.summary.totalTests} | Passed: ${auditResults.summary.passed} | Failed: ${auditResults.summary.failed}`, 
      auditResults.summary.failed === 0 ? 'success' : 'error');
    
    setResults(auditResults);
    setCurrentTest('Audit complete');
    setIsRunning(false);
    
    // Save to backend
    try {
      await fetch(`${API}/admin/audit-report`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(auditResults)
      });
    } catch {
      // Silently fail - report is still available locally
    }
  };

  // Run full E2E audit with Playwright
  const runE2EAudit = async () => {
    setIsRunningE2E(true);
    setE2ELogs([]);
    setE2EResults(null);
    
    addE2ELog('Starting comprehensive E2E audit with Playwright...', 'info');
    addE2ELog('This will take 2-5 minutes. Please wait...', 'info');
    
    try {
      const response = await fetch(`${API}/admin/health-audit/run-e2e`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          admin_email: user?.email,
          admin_password: null // Don't send password, server uses current session
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        addE2ELog('✓ E2E Audit completed successfully!', 'success');
        setE2EResults(data.report);
        toast.success('E2E Audit completed!');
        
        // Log summary
        const summary = data.report?.summary || {};
        addE2ELog(`Total Routes: ${summary.total_routes || 0}`, 'info');
        addE2ELog(`Passed: ${summary.passed || 0}`, 'success');
        addE2ELog(`Failed: ${summary.failed || 0}`, summary.failed > 0 ? 'error' : 'success');
        addE2ELog(`Warnings: ${summary.warnings || 0}`, summary.warnings > 0 ? 'warning' : 'info');
        
        // Log recommendations
        if (data.report?.recommendations?.length > 0) {
          addE2ELog('--- Recommendations ---', 'info');
          data.report.recommendations.forEach(rec => {
            const type = rec.includes('CRITICAL') ? 'error' : 
                        rec.includes('HIGH') ? 'error' :
                        rec.includes('MEDIUM') ? 'warning' : 'info';
            addE2ELog(rec, type);
          });
        }
      } else {
        addE2ELog(`✗ E2E Audit failed: ${data.error}`, 'error');
        if (data.traceback) {
          addE2ELog(`Traceback: ${data.traceback.substring(0, 500)}`, 'error');
        }
        toast.error('E2E Audit failed');
      }
    } catch (err) {
      addE2ELog(`✗ Network error: ${err.message}`, 'error');
      toast.error('Failed to run E2E audit');
    } finally {
      setIsRunningE2E(false);
    }
  };

  const exportReport = (reportData, filename) => {
    if (!reportData) return;
    
    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || `audit-report-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Report exported');
  };

  const getStatusBadge = (ok, status) => {
    if (ok) {
      return <Badge className="bg-green-500/20 text-green-500"><CheckCircle className="w-3 h-3 mr-1" /> PASS</Badge>;
    }
    if (status === 401 || status === 403) {
      return <Badge className="bg-yellow-500/20 text-yellow-500"><AlertTriangle className="w-3 h-3 mr-1" /> AUTH</Badge>;
    }
    return <Badge className="bg-red-500/20 text-red-500"><XCircle className="w-3 h-3 mr-1" /> FAIL</Badge>;
  };

  if (!isAuthenticated || (user?.role !== 'owner' && user?.email !== 'itzchopbusiness1@gmail.com')) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background p-6" data-testid="health-audit-page">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-primary" />
            <div>
              <h1 className="text-2xl font-bold">Platform Health Audit</h1>
              <p className="text-sm text-muted-foreground">Comprehensive error detection and E2E testing</p>
            </div>
          </div>
        </div>

        {/* Audit Mode Tabs */}
        <Tabs defaultValue="quick" className="space-y-4">
          <TabsList className="grid grid-cols-3 w-[500px]">
            <TabsTrigger value="quick" className="flex items-center gap-2">
              <Zap className="w-4 h-4" />
              Quick API Audit
            </TabsTrigger>
            <TabsTrigger value="e2e" className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Full E2E Crawl
            </TabsTrigger>
            <TabsTrigger value="schedule" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Auto Schedule
            </TabsTrigger>
          </TabsList>

          {/* Quick API Audit Tab */}
          <TabsContent value="quick" className="space-y-4">
            <Card className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold">Quick API Health Check</h3>
                  <p className="text-sm text-muted-foreground">
                    Tests all routes and API endpoints for HTTP status codes. Fast (~30 seconds).
                  </p>
                </div>
                <div className="flex gap-2">
                  {results && (
                    <Button variant="outline" onClick={() => exportReport(results, 'quick-audit.json')}>
                      <Download className="w-4 h-4 mr-2" />
                      Export
                    </Button>
                  )}
                  <Button onClick={runQuickAudit} disabled={isRunning}>
                    {isRunning ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Running...
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4 mr-2" />
                        Run Quick Audit
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Progress */}
              {isRunning && (
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span>{currentTest}</span>
                    <span>{Math.round(progress)}%</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                </div>
              )}

              {/* Summary Cards */}
              {results && (
                <div className="grid grid-cols-4 gap-4 mb-4">
                  <Card className="p-3 text-center bg-muted/30">
                    <div className="text-2xl font-bold">{results.summary.totalTests}</div>
                    <div className="text-xs text-muted-foreground">Total</div>
                  </Card>
                  <Card className="p-3 text-center bg-green-500/10 border-green-500/30">
                    <div className="text-2xl font-bold text-green-500">{results.summary.passed}</div>
                    <div className="text-xs text-muted-foreground">Passed</div>
                  </Card>
                  <Card className="p-3 text-center bg-red-500/10 border-red-500/30">
                    <div className="text-2xl font-bold text-red-500">{results.summary.failed}</div>
                    <div className="text-xs text-muted-foreground">Failed</div>
                  </Card>
                  <Card className="p-3 text-center">
                    <div className={`text-2xl font-bold ${results.summary.failed === 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {results.summary.failed === 0 ? '100%' : `${Math.round((results.summary.passed / results.summary.totalTests) * 100)}%`}
                    </div>
                    <div className="text-xs text-muted-foreground">Pass Rate</div>
                  </Card>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                {/* Logs */}
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Audit Log
                  </h4>
                  <div className="h-[300px] overflow-y-auto bg-background/50 rounded-lg p-3 font-mono text-xs space-y-1">
                    {logs.length === 0 ? (
                      <div className="text-muted-foreground">Click "Run Quick Audit" to start...</div>
                    ) : (
                      logs.map((log, idx) => (
                        <div 
                          key={idx} 
                          className={`${
                            log.type === 'success' ? 'text-green-400' : 
                            log.type === 'error' ? 'text-red-400' : 
                            log.type === 'warning' ? 'text-yellow-400' : 
                            'text-gray-400'
                          }`}
                        >
                          <span className="text-gray-500">[{log.timestamp}]</span> {log.message}
                        </div>
                      ))
                    )}
                    <div ref={logsEndRef} />
                  </div>
                </div>

                {/* Results Table */}
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Server className="w-4 h-4" /> Results
                  </h4>
                  {results ? (
                    <Tabs defaultValue="api" className="h-[300px]">
                      <TabsList className="mb-2 h-8">
                        <TabsTrigger value="api" className="text-xs">API</TabsTrigger>
                        <TabsTrigger value="public" className="text-xs">Public</TabsTrigger>
                        <TabsTrigger value="auth" className="text-xs">Auth</TabsTrigger>
                        <TabsTrigger value="dashboard" className="text-xs">Dashboard</TabsTrigger>
                      </TabsList>
                      
                      <TabsContent value="api" className="h-[250px] overflow-y-auto">
                        <div className="space-y-1">
                          {results.apiEndpoints.map((r, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-muted/30 rounded text-sm">
                              <div>
                                <span className="font-medium">{r.name}</span>
                                <span className="text-xs text-muted-foreground ml-2">{r.method} {r.path}</span>
                              </div>
                              {getStatusBadge(r.ok, r.status)}
                            </div>
                          ))}
                        </div>
                      </TabsContent>
                      
                      <TabsContent value="public" className="h-[250px] overflow-y-auto">
                        <div className="space-y-1">
                          {results.publicRoutes.map((r, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-muted/30 rounded text-sm">
                              <span>{r.name}</span>
                              {getStatusBadge(r.ok, r.status)}
                            </div>
                          ))}
                        </div>
                      </TabsContent>
                      
                      <TabsContent value="auth" className="h-[250px] overflow-y-auto">
                        <div className="space-y-1">
                          {results.authRoutes.map((r, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-muted/30 rounded text-sm">
                              <span>{r.name}</span>
                              {getStatusBadge(r.ok, r.status)}
                            </div>
                          ))}
                        </div>
                      </TabsContent>
                      
                      <TabsContent value="dashboard" className="h-[250px] overflow-y-auto">
                        <div className="space-y-1">
                          {results.dashboardRoutes.map((r, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-muted/30 rounded text-sm">
                              <span>{r.name}</span>
                              {getStatusBadge(r.ok, r.status)}
                            </div>
                          ))}
                        </div>
                      </TabsContent>
                    </Tabs>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-muted-foreground bg-muted/20 rounded-lg">
                      <div className="text-center">
                        <Globe className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Run audit to see results</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* E2E Crawl Tab */}
          <TabsContent value="e2e" className="space-y-4">
            <Card className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold flex items-center gap-2">
                    <Eye className="w-5 h-5 text-purple-500" />
                    Full E2E Platform Crawl
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Uses Playwright to crawl all routes, click elements, and capture console errors & network failures.
                    Takes 2-5 minutes.
                  </p>
                </div>
                <div className="flex gap-2">
                  {e2eResults && (
                    <Button variant="outline" onClick={() => exportReport(e2eResults, 'e2e-audit.json')}>
                      <Download className="w-4 h-4 mr-2" />
                      Export
                    </Button>
                  )}
                  <Button onClick={runE2EAudit} disabled={isRunningE2E} className="bg-purple-600 hover:bg-purple-700">
                    {isRunningE2E ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Crawling...
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4 mr-2" />
                        Run E2E Audit
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* E2E Results Summary */}
              {e2eResults && (
                <div className="space-y-4 mb-4">
                  <div className="grid grid-cols-5 gap-3">
                    <Card className="p-3 text-center bg-muted/30">
                      <div className="text-2xl font-bold">{e2eResults.summary?.total_routes || 0}</div>
                      <div className="text-xs text-muted-foreground">Routes</div>
                    </Card>
                    <Card className="p-3 text-center bg-green-500/10 border-green-500/30">
                      <div className="text-2xl font-bold text-green-500">{e2eResults.summary?.passed || 0}</div>
                      <div className="text-xs text-muted-foreground">Passed</div>
                    </Card>
                    <Card className="p-3 text-center bg-red-500/10 border-red-500/30">
                      <div className="text-2xl font-bold text-red-500">{e2eResults.summary?.failed || 0}</div>
                      <div className="text-xs text-muted-foreground">Failed</div>
                    </Card>
                    <Card className="p-3 text-center bg-yellow-500/10 border-yellow-500/30">
                      <div className="text-2xl font-bold text-yellow-500">{e2eResults.summary?.warnings || 0}</div>
                      <div className="text-xs text-muted-foreground">Warnings</div>
                    </Card>
                    <Card className="p-3 text-center bg-orange-500/10 border-orange-500/30">
                      <div className="text-2xl font-bold text-orange-500">{e2eResults.console_errors?.length || 0}</div>
                      <div className="text-xs text-muted-foreground">Console Errors</div>
                    </Card>
                  </div>

                  {/* Recommendations */}
                  {e2eResults.recommendations?.length > 0 && (
                    <Card className="p-4 border-amber-500/30 bg-amber-500/5">
                      <h4 className="font-medium mb-2 flex items-center gap-2">
                        <Lightbulb className="w-4 h-4 text-amber-500" />
                        Recommendations
                      </h4>
                      <ul className="space-y-2 text-sm">
                        {e2eResults.recommendations.map((rec, idx) => (
                          <li key={idx} className={`flex items-start gap-2 ${
                            rec.includes('CRITICAL') || rec.includes('HIGH') ? 'text-red-400' :
                            rec.includes('MEDIUM') ? 'text-yellow-400' : 'text-muted-foreground'
                          }`}>
                            <span className="mt-1">
                              {rec.includes('CRITICAL') || rec.includes('HIGH') ? (
                                <XCircle className="w-4 h-4" />
                              ) : rec.includes('MEDIUM') ? (
                                <AlertTriangle className="w-4 h-4" />
                              ) : (
                                <CheckCircle className="w-4 h-4 text-green-500" />
                              )}
                            </span>
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </Card>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                {/* E2E Logs */}
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> E2E Audit Log
                  </h4>
                  <div className="h-[350px] overflow-y-auto bg-background/50 rounded-lg p-3 font-mono text-xs space-y-1">
                    {e2eLogs.length === 0 ? (
                      <div className="text-muted-foreground">Click "Run E2E Audit" to start the Playwright crawl...</div>
                    ) : (
                      e2eLogs.map((log, idx) => (
                        <div 
                          key={idx} 
                          className={`${
                            log.type === 'success' ? 'text-green-400' : 
                            log.type === 'error' ? 'text-red-400' : 
                            log.type === 'warning' ? 'text-yellow-400' : 
                            'text-gray-400'
                          }`}
                        >
                          <span className="text-gray-500">[{log.timestamp}]</span> {log.message}
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Phase Results */}
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Bug className="w-4 h-4" /> Phase Results
                  </h4>
                  {e2eResults?.phases ? (
                    <div className="h-[350px] overflow-y-auto space-y-2">
                      {e2eResults.phases.map((phase, idx) => (
                        <Card key={idx} className="p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-sm">{phase.name}</span>
                            <Badge variant={phase.failed > 0 ? 'destructive' : 'default'}>
                              {phase.passed}/{phase.routes_tested || 0} passed
                            </Badge>
                          </div>
                          {phase.results && (
                            <div className="space-y-1">
                              {phase.results.slice(0, 5).map((r, ridx) => (
                                <div key={ridx} className="flex items-center justify-between text-xs py-1 border-b border-border">
                                  <span className="truncate max-w-[180px]">{r.name}</span>
                                  <span className={`${
                                    r.status === 'passed' ? 'text-green-400' :
                                    r.status === 'warning' ? 'text-yellow-400' :
                                    r.status === 'failed' ? 'text-red-400' : 'text-gray-400'
                                  }`}>
                                    {r.status} {r.load_time_ms ? `(${r.load_time_ms}ms)` : ''}
                                  </span>
                                </div>
                              ))}
                              {phase.results.length > 5 && (
                                <div className="text-xs text-muted-foreground text-center pt-1">
                                  +{phase.results.length - 5} more routes
                                </div>
                              )}
                            </div>
                          )}
                          {phase.status === 'skipped' && (
                            <p className="text-xs text-muted-foreground">{phase.reason}</p>
                          )}
                        </Card>
                      ))}
                      
                      {/* Console Errors */}
                      {e2eResults.console_errors?.length > 0 && (
                        <Card className="p-3 border-red-500/30">
                          <h5 className="font-medium text-sm text-red-400 mb-2">Console Errors ({e2eResults.console_errors.length})</h5>
                          <div className="space-y-1 max-h-[150px] overflow-y-auto">
                            {e2eResults.console_errors.slice(0, 10).map((err, idx) => (
                              <div key={idx} className="text-xs text-red-300 bg-red-500/10 p-1 rounded">
                                {err.text?.substring(0, 100) || err}
                              </div>
                            ))}
                          </div>
                        </Card>
                      )}
                    </div>
                  ) : (
                    <div className="h-[350px] flex items-center justify-center text-muted-foreground bg-muted/20 rounded-lg">
                      <div className="text-center">
                        <Eye className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Run E2E audit to see detailed results</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          <Clock className="w-3 h-3 inline mr-1" />
                          Takes 2-5 minutes
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Schedule Tab */}
          <TabsContent value="schedule" className="space-y-4">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="font-semibold flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-blue-500" />
                    Automated Audit Schedule
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Configure automatic E2E audits with email notifications for critical issues.
                  </p>
                </div>
                <Button onClick={saveSchedule} disabled={savingSchedule}>
                  {savingSchedule ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Schedule
                    </>
                  )}
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-6">
                {/* Left Column - Schedule Settings */}
                <div className="space-y-6">
                  {/* Enable/Disable Toggle */}
                  <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${schedule.enabled ? 'bg-green-500/20' : 'bg-muted'}`}>
                        <Clock className={`w-5 h-5 ${schedule.enabled ? 'text-green-500' : 'text-muted-foreground'}`} />
                      </div>
                      <div>
                        <div className="font-medium">Automatic Audits</div>
                        <div className="text-sm text-muted-foreground">
                          {schedule.enabled ? 'Enabled - Audits will run automatically' : 'Disabled - Manual only'}
                        </div>
                      </div>
                    </div>
                    <Switch
                      checked={schedule.enabled}
                      onCheckedChange={(checked) => setSchedule(prev => ({ ...prev, enabled: checked }))}
                    />
                  </div>

                  {/* Frequency */}
                  <div className="space-y-3">
                    <Label className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Frequency
                    </Label>
                    <div className="grid grid-cols-3 gap-2">
                      {['hourly', 'daily', 'weekly'].map((freq) => (
                        <Button
                          key={freq}
                          variant={schedule.frequency === freq ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setSchedule(prev => ({ ...prev, frequency: freq }))}
                          className="capitalize"
                        >
                          {freq}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Time Settings */}
                  {schedule.frequency !== 'hourly' && (
                    <div className="space-y-3">
                      <Label className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        Run at (UTC)
                      </Label>
                      <select
                        value={schedule.hour}
                        onChange={(e) => setSchedule(prev => ({ ...prev, hour: parseInt(e.target.value) }))}
                        className="w-full p-2 rounded-md bg-background border border-input"
                      >
                        {Array.from({ length: 24 }, (_, i) => (
                          <option key={i} value={i}>
                            {i.toString().padStart(2, '0')}:00 UTC ({new Date(Date.UTC(2000, 0, 1, i)).toLocaleTimeString('en-US', { hour: 'numeric', hour12: true, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone })})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Day of Week (for weekly) */}
                  {schedule.frequency === 'weekly' && (
                    <div className="space-y-3">
                      <Label>Day of Week</Label>
                      <div className="grid grid-cols-7 gap-1">
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, idx) => (
                          <Button
                            key={day}
                            variant={schedule.day_of_week === idx ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setSchedule(prev => ({ ...prev, day_of_week: idx }))}
                            className="text-xs"
                          >
                            {day}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Schedule Status */}
                  <Card className="p-4 bg-muted/20">
                    <h4 className="font-medium mb-2 text-sm">Schedule Status</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Last Run:</span>
                        <span>{schedule.last_run ? new Date(schedule.last_run).toLocaleString() : 'Never'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Next Run:</span>
                        <span className={schedule.enabled ? 'text-green-500' : 'text-muted-foreground'}>
                          {schedule.enabled && schedule.next_run 
                            ? new Date(schedule.next_run).toLocaleString() 
                            : 'Not scheduled'}
                        </span>
                      </div>
                    </div>
                  </Card>
                </div>

                {/* Right Column - Notification Settings */}
                <div className="space-y-6">
                  <div className="p-4 bg-muted/30 rounded-lg">
                    <h4 className="font-medium mb-4 flex items-center gap-2">
                      <Bell className="w-4 h-4 text-amber-500" />
                      Email Notifications
                    </h4>

                    {/* Notification Triggers */}
                    <div className="space-y-4 mb-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-medium">Critical Issues</div>
                          <div className="text-xs text-muted-foreground">Send email when CRITICAL or HIGH issues found</div>
                        </div>
                        <Switch
                          checked={schedule.notify_on_critical}
                          onCheckedChange={(checked) => setSchedule(prev => ({ ...prev, notify_on_critical: checked }))}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-medium">Any Failures</div>
                          <div className="text-xs text-muted-foreground">Send email for any failed routes</div>
                        </div>
                        <Switch
                          checked={schedule.notify_on_any_failure}
                          onCheckedChange={(checked) => setSchedule(prev => ({ ...prev, notify_on_any_failure: checked }))}
                        />
                      </div>
                    </div>

                    {/* Email Recipients */}
                    <div className="space-y-3">
                      <Label className="flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        Recipients
                      </Label>
                      
                      {/* Add Email Input */}
                      <div className="flex gap-2">
                        <Input
                          type="email"
                          placeholder="Enter email address"
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && addNotificationEmail()}
                          className="flex-1"
                        />
                        <Button 
                          variant="outline" 
                          size="icon"
                          onClick={addNotificationEmail}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>

                      {/* Email List */}
                      <div className="space-y-2 max-h-[200px] overflow-y-auto">
                        {schedule.notification_emails.length === 0 ? (
                          <div className="text-sm text-muted-foreground text-center py-4">
                            No email recipients added yet
                          </div>
                        ) : (
                          schedule.notification_emails.map((email, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-background rounded border">
                              <span className="text-sm truncate">{email}</span>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-red-500 hover:text-red-600"
                                onClick={() => removeNotificationEmail(email)}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Test Notification */}
                  <Card className="p-4 border-dashed">
                    <div className="text-center">
                      <Settings className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground mb-3">
                        Want to test your notification setup?
                      </p>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={runE2EAudit}
                        disabled={isRunningE2E || schedule.notification_emails.length === 0}
                      >
                        <Play className="w-4 h-4 mr-2" />
                        Run Test Audit
                      </Button>
                    </div>
                  </Card>
                </div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
